/**
 * Apple AirPlay via WebKit’s native video picker (`webkitShowPlaybackTargetPicker`).
 * Safari / WebKit on macOS, iPadOS, iOS — no backend; device handles discovery and playback.
 *
 * Limitation: Chromium’s Remote Playback API is separate from AirPlay; Chromecast is handled in `useChromecast`.
 */

export type VideoWithAirPlay = HTMLVideoElement & {
  webkitShowPlaybackTargetPicker?: () => void;
  webkitCurrentPlaybackTargetIsWireless?: boolean;
};

export function supportsWebKitAirPlayPicker(video: HTMLVideoElement | null): boolean {
  if (!video) return false;
  const w = video as VideoWithAirPlay;
  return typeof w.webkitShowPlaybackTargetPicker === 'function';
}

/** True if the engine exposes the picker on the prototype (Safari often does before src is set). */
export function webkitAirPlayPickerSupportedByEngine(): boolean {
  if (typeof HTMLVideoElement === 'undefined') return false;
  try {
    const proto = HTMLVideoElement.prototype as unknown as VideoWithAirPlay;
    if (typeof proto.webkitShowPlaybackTargetPicker === 'function') return true;
    /* Some WebKit builds only attach the picker to instances, not the prototype. */
    if (typeof document !== 'undefined') {
      const probe = document.createElement('video');
      const w = probe as VideoWithAirPlay;
      if (typeof w.webkitShowPlaybackTargetPicker === 'function') return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * macOS / iOS / iPadOS from UA — where users often expect an AirPlay affordance.
 * (Still requires WebKit for a working picker; used to show a disabled hint in Chrome, etc.)
 */
export function userAgentSuggestsAppleAirPlayContext(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /iPhone|iPad|iPod|Macintosh|Mac OS X|CPU iPhone OS|CPU OS/i.test(ua);
}

export function openWebKitAirPlayPicker(video: HTMLVideoElement | null): void {
  if (!video) return;
  const w = video as VideoWithAirPlay;
  if (typeof w.webkitShowPlaybackTargetPicker !== 'function') return;
  try {
    w.webkitShowPlaybackTargetPicker();
  } catch {
    /* user dismissed or environment blocked */
  }
}
